import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { getAuth, updatePassword } from 'firebase/auth';
import { Subject, filter, takeUntil } from 'rxjs';
import { matchValidator } from 'src/app/auth/sign-up/sign-up.component';
import { confirmModalData } from '../profile.service';
import { AuthService } from 'src/app/firebase/auth.service';
import { ProfileStore } from '../store/profile-store';
import {
  createToaster,
  positionType,
} from 'src/app/shared/components/modals/toaster';

@Component({
  selector: 'app-update-password',
  templateUrl: './update-password.component.html',
  styleUrls: ['./update-password.component.scss'],
})
export class UpdatePasswordComponent implements OnInit, OnDestroy {
  @Input() showPasswordForm: boolean;
  @Output() showPasswordFormChange = new EventEmitter<boolean>();
  @Output() update: EventEmitter<boolean> = new EventEmitter();
  @Output() cancel: EventEmitter<boolean> = new EventEmitter();

  form: FormGroup;
  loading = false;
  type: string = 'password';
  typeConfirm: string = 'password';
  typeOld: string = 'password';

  iconName: string = 'eye';
  iconNameConfirm: string = 'eye';
  iconNameOld: string = 'eye';

  showConfirmModal = false;
  confirmModalData = confirmModalData;

  userId: string;
  user;
  authUser;

  private destroy$ = new Subject();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private profileStore: ProfileStore,
    private activeRouter: ActivatedRoute
  ) {}

  ngOnInit() {
    this.userId = this.activeRouter.snapshot.params.id;
    this.profileStore.getItemByByValue({ uid: this.userId });
    this.authUser = getAuth().currentUser;
    this.listenUserChanges();
    this.form = this.fb.group(
      {
        oldPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            matchValidator('confirmPassword', true),
          ],
        ],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            matchValidator('confirmPassword', true),
          ],
        ],
        repeatPassword: ['', [Validators.required, matchValidator('password')]],
      },
      {
        validators: [matchValidator],
      }
    );
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  async handleUpdate() {
    this.loading = true;

    try {
      // First verify the old password by attempting to sign in
      try {
        await this.authService.signIn({
          email: this.user.email,
          password: this.form.get('oldPassword').value,
          rememberMe: true,
        });
      } catch (error) {
        throw new Error('Incorrect old password. Please verify and try again.');
      }

      // If old password is correct, update to new password
      await updatePassword(this.authUser, this.form.get('password').value);

      this.loading = false;
      this.form.reset();
      createToaster(
        'Password updated successfully!',
        'success',
        positionType.BOTTOM
      );
      this.showPasswordForm = false;
      this.showPasswordFormChange.emit(false);
      this.update.emit(true);
    } catch (error) {
      this.loading = false;
      const errorMessage =
        error?.message || 'An error occurred while updating password';
      createToaster(errorMessage, 'danger', positionType.BOTTOM);
    }
  }

  handleCancel() {
    this.form.reset();
    this.showPasswordForm = false;
    this.showPasswordFormChange.emit(false);
    this.cancel.emit(true);
  }

  changeType() {
    this.type = this.type === 'password' ? 'text' : 'password';
    this.iconName = this.iconName === 'eye' ? 'eye-off' : 'eye';
  }

  changeTypeConfirm() {
    this.typeConfirm = this.typeConfirm === 'password' ? 'text' : 'password';
    this.iconNameConfirm = this.iconNameConfirm === 'eye' ? 'eye-off' : 'eye';
  }

  changeTypeOld() {
    this.typeOld = this.typeOld === 'password' ? 'text' : 'password';
    this.iconNameOld = this.iconNameOld === 'eye' ? 'eye-off' : 'eye';
  }

  private listenUserChanges() {
    this.profileStore
      .selectItem()
      .pipe(
        filter(user => !!user),
        takeUntil(this.destroy$)
      )
      .subscribe(user => (this.user = user));
  }
}

